'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { ChannelChip } from './ChannelChip';

const steps = [
  {
    num: 1,
    label: 'Pick',
    color: '#EF4444',
    title: 'Pick a template',
    desc: 'Start from a proven check-in — sales, standups, pulse, field crews. Every question is yours to edit.',
    features: [
      '5 templates written by people who run these meetings',
      'Edit, add, reorder questions',
      'Set the cadence — daily, weekly, your call',
    ],
    visualLabel: 'Choosing a check-in template and setting its cadence',
    gradient: 'from-[#EF4444]/15 via-[#F97316]/10 to-transparent',
    reverse: false,
  },
  {
    num: 2,
    label: 'Invite',
    color: '#3B82F6',
    title: 'Invite your team',
    desc: 'Add names, emails, or phone numbers. Members never need an account or an app — Tessa texts a heads-up before her first call.',
    features: [
      'Members are free, always',
      'Instant membership — no invite-accept dance',
      'Anyone can say SKIP; Tessa respects it',
    ],
    visualLabel: 'Adding team members by name, email, or phone number',
    gradient: 'from-[#3B82F6]/15 via-[#8B5CF6]/10 to-transparent',
    reverse: true,
  },
  {
    num: 3,
    label: 'Digest',
    color: '#10B981',
    title: 'Tessa calls. You read the digest.',
    desc: 'On schedule, Tessa reaches each person on their best channel, asks your questions, listens, and reports back.',
    features: [
      'Full transcripts + the 30-second summary',
      'Trends across weeks',
      "Reply to the digest to steer next week's questions",
    ],
    visualLabel: 'Reading a digest of what Tessa heard from the team',
    gradient: 'from-[#10B981]/15 via-[#3B82F6]/10 to-transparent',
    reverse: false,
  },
];

// ---------------------------------------------------------------------------
// Step mocks (Adoption UX wave) — the three visual panels used to be empty
// gradient decoration; they now carry miniature product mocks, matching the
// house pattern of every neighboring section (Tessa contact card, connector
// chat bubble, HEAR-IT-WORK audio rows): hand-built HTML, never screenshots.
// All mock internals are aria-hidden — the panel-level label tells the story.
// ---------------------------------------------------------------------------

const monoLabel = { fontFamily: 'var(--font-space-mono)' } as const;

/** Step 1 — a mini template picker: real templates, one selected w/ cadence. */
function TemplatePickerMock() {
  return (
    <div className="w-full max-w-[340px] rounded-xl border border-border bg-card p-3.5 shadow-lg space-y-2">
      <div className="text-[10px] uppercase tracking-[1.5px] text-muted-foreground" style={monoLabel}>
        Pick a template
      </div>
      <div className="rounded-lg border-2 bg-background p-2 sm:p-2.5" style={{ borderColor: '#EF4444' }}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-foreground">Weekly Sales Check-in</div>
            <div className="truncate text-[11px] text-muted-foreground">4 questions · ~10 min · phone</div>
          </div>
          {/* red-600, not brand #EF4444: white text at 10px needs ≥4.5:1 (review MED) */}
          <span
            className="whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-semibold text-white"
            style={{ background: '#DC2626' }}
          >
            Fridays at 4 PM
          </span>
        </div>
      </div>
      <div className="rounded-lg border border-border p-2 opacity-70 sm:p-2.5">
        <div className="truncate text-[13px] font-medium text-foreground">Team Standup</div>
        <div className="truncate text-[11px] text-muted-foreground">3 questions · ~5 min · room</div>
      </div>
      <div className="hidden rounded-lg border border-border p-2.5 opacity-70 sm:block">
        <div className="truncate text-[13px] font-medium text-foreground">Maintenance Crew Check-in</div>
        <div className="truncate text-[11px] text-muted-foreground">4 questions · ~5 min · phone</div>
      </div>
    </div>
  );
}

/** Step 2 — a mini invite form: two added members + one row mid-entry. */
function InviteMock() {
  return (
    <div className="w-full max-w-[340px] rounded-xl border border-border bg-card p-3.5 shadow-lg space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[1.5px] text-muted-foreground" style={monoLabel}>
          Invite your team
        </div>
        {/* emerald-800 on the pastel: #059669 was 3.58:1 at 10px (review MED) */}
        <span
          className="whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ background: '#ECFDF5', color: '#065F46' }}
        >
          Members are free
        </span>
      </div>
      <div className="flex items-center gap-2.5 rounded-lg border border-border p-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ background: '#EFF6FF', color: '#3B82F6' }}
        >
          M
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">Maria Lopez</span>
        <span className="flex gap-1">
          <ChannelChip channel="phone" />
          <ChannelChip channel="whatsapp" />
        </span>
      </div>
      <div className="flex items-center gap-2.5 rounded-lg border border-border p-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ background: '#F5F3FF', color: '#8B5CF6' }}
        >
          J
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">James Chen</span>
        <span className="flex gap-1">
          <ChannelChip channel="email" />
          <ChannelChip channel="slack" />
        </span>
      </div>
      <div className="hidden items-center gap-2.5 rounded-lg border border-dashed border-border p-2 sm:flex">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-[13px] text-muted-foreground">
          +
        </span>
        <span className="text-[13px] text-muted-foreground">
          +1 (415) 555-01
          {/* motion-safe: reduced-motion users get a static caret (review LOW) */}
          <span className="ml-px inline-block h-[13px] w-px translate-y-[2px] bg-foreground motion-safe:animate-pulse" />
        </span>
      </div>
    </div>
  );
}

/** Step 3 — the Monday digest: the same line the hero's audio row promises. */
function DigestMock() {
  return (
    <div className="w-full max-w-[340px] rounded-xl border border-border bg-card p-3 shadow-lg space-y-2.5 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[1.5px] text-muted-foreground" style={monoLabel}>
          Your Monday digest
        </div>
        {/* theme-aware: #10B981 was 2.54:1 on the light card (review MED) */}
        <span className="whitespace-nowrap text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
          ↑ 38% response rate
        </span>
      </div>
      <div className="text-[14px] font-semibold leading-snug text-foreground">
        2 blockers surfaced. Maria needs sign-off by Thursday.
      </div>
      {/* Bullets appear ≥sm only — at 320px their wrapping overflowed the
          fixed 240px panel (review LOW); headline + trend + reply carry the
          story on the smallest screens. */}
      <div className="hidden space-y-1.5 sm:block">
        <div className="flex items-start gap-2 text-[11.5px] leading-snug text-muted-foreground">
          <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: '#F59E0B' }} />
          Meridian renewal at risk — their champion just left
        </div>
        <div className="flex items-start gap-2 text-[11.5px] leading-snug text-muted-foreground">
          <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: '#F59E0B' }} />
          Pricing approval is holding up two deals
        </div>
      </div>
      <div className="border-t border-border pt-2 text-[11px] text-muted-foreground">
        Reply to steer next week&apos;s questions
      </div>
    </div>
  );
}

const STEP_MOCKS: Record<number, () => JSX.Element> = {
  1: TemplatePickerMock,
  2: InviteMock,
  3: DigestMock,
};

/** The gradient panel shell — unchanged backdrop, now hosting a mock. When
 *  interactive (wrapped in a labeled Link), the link is the single accessible
 *  element — no role="img" here, or screen readers announce it twice. */
function StepPanel({ step, interactive }: { step: (typeof steps)[number]; interactive?: boolean }) {
  const Mock = STEP_MOCKS[step.num];
  return (
    <div
      {...(interactive ? {} : { role: 'img', 'aria-label': step.visualLabel })}
      className={`relative overflow-hidden rounded-[16px] border border-border h-[240px] sm:h-[300px] md:h-[360px] bg-gradient-to-br ${step.gradient} ${
        interactive ? 'transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-md' : ''
      }`}
    >
      <div
        className="absolute right-[-60px] top-[-60px] h-[220px] w-[220px] rounded-full opacity-40 blur-[50px]"
        style={{ background: step.color }}
      />
      <div
        className="absolute bottom-[-80px] left-[-40px] h-[200px] w-[200px] rounded-full opacity-20 blur-[60px]"
        style={{ background: step.color }}
      />
      {/* The mock floats above the gradient; aria-hidden so the panel label
          (or the wrapping link) is the single accessible description. */}
      <div aria-hidden="true" className="relative z-10 flex h-full items-center justify-center p-4 sm:p-6">
        <Mock />
      </div>
      {interactive && (
        // ALWAYS visible (review MED: hover-gated meant touch + keyboard users
        // never learned this panel is the on-ramp); hover/focus just brightens
        // it. aria-hidden — the Link's own label already says this (review LOW:
        // it was duplicated in the a11y tree).
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-foreground px-3 py-1 text-[11px] font-medium text-background opacity-90 shadow-md transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          Try it — no account needed →
        </div>
      )}
    </div>
  );
}

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-20 bg-card border-t border-b border-border">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-13">
          <div className="landing-section-label">How it works</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Three steps. Then it runs itself.
          </h2>
          <p className="text-[17px] text-muted-foreground max-w-[540px] mx-auto">
            You set it up once. Tessa handles every check-in after that.
          </p>
        </div>

        <div className="space-y-0">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-14 items-center py-14 ${
                i < steps.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: step.reverse ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={step.reverse ? 'lg:order-2' : ''}
              >
                <div className="inline-flex items-center gap-2 mb-4" style={monoLabel}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[13px]"
                    style={{ background: step.color }}
                  >
                    {step.num}
                  </div>
                  <span className="text-[11px] uppercase tracking-[1.5px]" style={{ color: step.color }}>
                    {step.label}
                  </span>
                </div>
                <h3 className="text-[30px] font-bold tracking-[-1px] mb-3 text-foreground">
                  {step.title}
                </h3>
                <p className="text-[16px] text-muted-foreground leading-[1.7] mb-6">
                  {step.desc}
                </p>
                <ul className="space-y-2.5">
                  {step.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[14px] text-foreground">
                      <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Step visual — step 1 is a live on-ramp into the real template
                  picker (public, no account needed); steps 2–3 are static. */}
              <motion.div
                initial={{ opacity: 0, x: step.reverse ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className={step.reverse ? 'lg:order-1' : ''}
              >
                {step.num === 1 ? (
                  <Link
                    href="/routines/new"
                    aria-label="Pick a check-in template — try it, no account needed"
                    // focus-visible ring so keyboard users see the on-ramp
                    // (review MED: there was no focus affordance at all).
                    className="group block rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <StepPanel step={step} interactive />
                  </Link>
                ) : (
                  <StepPanel step={step} />
                )}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
